// Numeric/byte limits and protocol constants referenced by the zod schemas
// and by every package that talks to the API. These are the single source
// of truth: do not redeclare these magic numbers anywhere else.
//
// _Requirements: 8.1, 9.6, 11.4, 15.2_

/** Maximum UTF-8 character count for a Text_Channel message. (Req 8.1) */
export const TEXT_MESSAGE_MAX_CHARS = 4000;

/** Maximum byte size of a Snippets_Channel message body. (Req 9.6) */
export const SNIPPET_MAX_BYTES = 64 * 1024; // 64 KB

/**
 * Snippet rendering threshold above which the Web_Client collapses by default.
 * (Req 9.8) Kept here so the API and UI agree on what "long" means.
 */
export const SNIPPET_COLLAPSE_LINES = 200;

/** Maximum file size for a single attachment in v1 for free Accounts. (Req 11.4) */
export const FILE_MAX_BYTES = 25 * 1024 * 1024; // 25 MB

/** Maximum number of participants in a group DM, including the initiator. (Req 15.2) */
export const DM_MAX_PARTICIPANTS = 10;

/** Length of the BIP-39 Recovery_Mnemonic used for Identity_Key derivation. (Req 1.1) */
export const MNEMONIC_WORD_COUNT = 12;

/** Token-bucket rate limit: messages per minute per Account. (Req 22.6) */
export const RATE_LIMIT_MESSAGES_PER_MIN = 60;

/** Token-bucket rate limit: messages per hour per Account. (Req 22.6) */
export const RATE_LIMIT_MESSAGES_PER_HOUR = 600;

/** Token-bucket rate limit: file uploads per minute per Account. (Req 22.6) */
export const RATE_LIMIT_UPLOADS_PER_MIN = 5;

/** Maximum age of a session challenge before the API_Server rejects it. (Req 2.5) */
export const CHALLENGE_MAX_AGE_SECONDS = 60;

/** Lifetime of an authenticated session before a new login is required. */
export const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Window during which an author may edit their own message. (Req 8.7) */
export const MESSAGE_EDIT_WINDOW_HOURS = 24;

/** Maximum lifetime of a Space invite link. (Req 6.2) */
export const INVITE_MAX_TTL_SECONDS = 7 * 24 * 60 * 60; // 7 days

/** TTL for signed download URLs issued by the API_Server. (Req 11.9) */
export const DOWNLOAD_URL_TTL_SECONDS = 15 * 60; // 15 minutes

/** Retention window for the realtime outbox before resync_required(too_old). (Property 14) */
export const REALTIME_OUTBOX_RETENTION_SECONDS = 7 * 24 * 60 * 60;

/** Account-deletion grace window before hard purge. (Req 20.2) */
export const ACCOUNT_DELETION_GRACE_SECONDS = 7 * 24 * 60 * 60;

/** Allowed Channel_Type values. (Req 5.2 / Glossary) */
export const CHANNEL_TYPES = ["text", "snippets", "secrets", "files"] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];

/** Allowed Channel_Type values inside a DM (no `files` per Req 15.5). */
export const DM_CHANNEL_TYPES = ["text", "snippets", "secrets"] as const;
export type DmChannelType = (typeof DM_CHANNEL_TYPES)[number];

/** Allowed Space_Type values. (Glossary) */
export const SPACE_TYPES = [
  "personal_workspace",
  "shared_workspace",
  "community",
] as const;
export type SpaceType = (typeof SPACE_TYPES)[number];

/** Allowed message storage kinds, mirroring the `message_storage` Postgres enum. */
export const MESSAGE_STORAGE_KINDS = ["e2e", "atrest"] as const;
export type MessageStorageKind = (typeof MESSAGE_STORAGE_KINDS)[number];

/** Sub-kind of an E2E envelope, mirroring the messages_e2e.envelope_kind column. */
export const ENVELOPE_KINDS = ["e2e", "secrets"] as const;
export type EnvelopeKind = (typeof ENVELOPE_KINDS)[number];

/** Platform tags reported on login, mirroring the `platform_kind` Postgres enum. */
export const PLATFORM_KINDS = ["web", "desktop", "ios", "android"] as const;
export type PlatformKind = (typeof PLATFORM_KINDS)[number];

/** Account lifecycle status values. */
export const ACCOUNT_STATUSES = [
  "active",
  "pending_deletion",
  "banned",
  "tombstoned",
] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/**
 * Account-level identity badges that can be issued by Link's control plane.
 * Community roles and server tags deliberately do not belong in this list.
 */
export const ACCOUNT_BADGES = [
  { id: "link-staff", label: "Link Staff", description: "Verified member of the Link team." },
  { id: "founder", label: "Founder", description: "Founder of Link." },
  { id: "bug-hunter", label: "Bug Hunter", description: "Reported a verified security issue." },
  { id: "early-user", label: "Early User", description: "Joined during Link's launch period." },
  { id: "og-supporter", label: "OG Supporter", description: "Supported Link during its first month." },
  { id: "link-plus", label: "Link Plus", description: "Active Link Plus subscriber." },
] as const;

export type AccountBadgeId = (typeof ACCOUNT_BADGES)[number]["id"];
export const ACCOUNT_BADGE_IDS = ACCOUNT_BADGES.map((badge) => badge.id) as ReadonlyArray<AccountBadgeId>;

/** Friend-edge status values. */
export const FRIEND_STATUSES = ["pending", "accepted", "blocked"] as const;
export type FriendStatus = (typeof FRIEND_STATUSES)[number];

/** Abuse report reason categories. */
export const ABUSE_REASONS = [
  "spam",
  "harassment",
  "csam",
  "illegal",
  "other",
] as const;
export type AbuseReason = (typeof ABUSE_REASONS)[number];

/** Operator audit log action categories. */
export const OPERATOR_ACTIONS = [
  "view_report",
  "view_message",
  "ban",
  "unban",
  "delete_message",
  "delete_space",
  "export_request",
] as const;
export type OperatorAction = (typeof OPERATOR_ACTIONS)[number];

/** Allowed expiration choices for Secrets_Channel messages. (Req 10.6) */
export const SECRETS_EXPIRATIONS = [
  "24h",
  "7d",
  "30d",
  "never",
] as const;
export type SecretsExpiration = (typeof SECRETS_EXPIRATIONS)[number];

/** Default expiration applied when the author makes no selection. (Req 10.6) */
export const SECRETS_DEFAULT_EXPIRATION: SecretsExpiration = "30d";

/** Permission bit flags for Community roles. (Req 7.2) */
export const ROLE_PERMISSIONS = {
  // Bits 0-7 shipped in the original schema and must never be reused.
  manage_channels: 1 << 0,
  manage_roles: 1 << 1,
  manage_members: 1 << 2,
  kick_members: 1 << 3,
  ban_members: 1 << 4,
  read_messages: 1 << 5,
  send_messages: 1 << 6,
  manage_messages: 1 << 7,
  // Link 0.1 additions. Keep this registry append-only.
  view_channels: 1 << 8,
  manage_server: 1 << 9,
  create_invites: 1 << 10,
  manage_invites: 1 << 11,
  change_nickname: 1 << 12,
  manage_nicknames: 1 << 13,
  view_audit_log: 1 << 14,
  pin_messages: 1 << 15,
  add_reactions: 1 << 16,
  mention_everyone: 1 << 17,
  create_expressions: 1 << 18,
  manage_expressions: 1 << 19,
  connect: 1 << 20,
  speak: 1 << 21,
  video: 1 << 22,
  stream: 1 << 23,
} as const;
export type RolePermissionFlag = keyof typeof ROLE_PERMISSIONS;

/** Union of every permission Link currently understands. */
export const ALL_ROLE_PERMISSIONS = Object.values(ROLE_PERMISSIONS).reduce(
  (mask, bit) => mask | bit,
  0,
);

/** Conservative permissions every community member starts with. */
export const DEFAULT_COMMUNITY_PERMISSIONS =
  ROLE_PERMISSIONS.view_channels |
  ROLE_PERMISSIONS.read_messages |
  ROLE_PERMISSIONS.send_messages |
  ROLE_PERMISSIONS.add_reactions |
  ROLE_PERMISSIONS.create_invites |
  ROLE_PERMISSIONS.change_nickname |
  ROLE_PERMISSIONS.connect |
  ROLE_PERMISSIONS.speak |
  ROLE_PERMISSIONS.video |
  ROLE_PERMISSIONS.stream;

export function hasRolePermission(mask: number, permission: RolePermissionFlag): boolean {
  return (mask & ROLE_PERMISSIONS[permission]) !== 0;
}
